// EXPORTS: useComparePhotos, IComparePhoto, getPhotoNodes
import { useMemo, useState, useEffect, useCallback } from 'react';
import { userStorage, getCurrentUserId } from '@/lib/user-storage';
import { useCustomPlan } from './useAppState';

const STORAGE_KEY = 'cycle_compare_photos_v2';
const LEGACY_BEFORE_KEY = 'cycle_compare_before';
const LEGACY_AFTER_KEY = 'cycle_compare_after';
const LEGACY_BEFORE_DATE_KEY = 'cycle_compare_before_date';

export interface IComparePhoto {
  id: string;               // 节点 day 作为 id，如 'day1' / 'day10'
  nodeDay: number;          // 属于方案第几天的节点（1 / 10 / 20 / 30...）
  dataUrl: string;          // 压缩后的 base64
  takenDate: string;        // 实际拍摄日期 YYYY-MM-DD
}

// 计算所有拍照节点（第1天 + 每10天）
export function getPhotoNodes(cycleTotalDays: number, currentCycle: number): number[] {
  const nodes: number[] = [1]; // 起点照
  for (let i = 1; i <= currentCycle; i++) {
    nodes.push(i * cycleTotalDays);
  }
  return nodes;
}

// 压缩图片：最大边 1080px，JPEG 0.8
export async function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        const maxDim = 1080;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(reader.result as string); return; }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useComparePhotos() {
  const { plan, cycleDayDisplay, currentCycleDisplay } = useCustomPlan();
  const [photos, setPhotos] = useState<IComparePhoto[]>([]);
  const [loaded, setLoaded] = useState(false);

  // 从 localStorage 加载
  useEffect(() => {
    try {
      const raw = userStorage.getItem(STORAGE_KEY);
      if (raw) {
        setPhotos(JSON.parse(raw));
        setLoaded(true);
        return;
      }

      // v1 旧数据迁移：before 作为起点照（day1），after 作为当前周期结束照
      const legacyBefore = userStorage.getItem(LEGACY_BEFORE_KEY);
      const legacyAfter = userStorage.getItem(LEGACY_AFTER_KEY);
      const legacyBeforeDate = userStorage.getItem(LEGACY_BEFORE_DATE_KEY);
      const migrated: IComparePhoto[] = [];

      if (legacyBefore) {
        migrated.push({
          id: 'day1',
          nodeDay: 1,
          dataUrl: legacyBefore,
          takenDate: legacyBeforeDate || plan.startDate,
        });
      }
      if (legacyAfter && plan.cycleTotalDays) {
        const nodeDay = currentCycleDisplay * plan.cycleTotalDays;
        migrated.push({
          id: `day${nodeDay}`,
          nodeDay,
          dataUrl: legacyAfter,
          takenDate: new Date().toISOString().slice(0, 10),
        });
      }

      if (migrated.length > 0) {
        setPhotos(migrated);
        userStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
      }
    } catch {
      // 忽略解析错误
    } finally {
      setLoaded(true);
    }
   }, [plan.startDate, currentCycleDisplay, plan.cycleTotalDays]);

  // 持久化
  const persist = useCallback((next: IComparePhoto[]) => {
    setPhotos(next);
    userStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, []);

  // 上传/更新某节点照片
  const uploadPhoto = useCallback(async (nodeDay: number, file: File) => {
    const owner = getCurrentUserId();
    const dataUrl = await compressImage(file);
    if (getCurrentUserId() !== owner) throw new Error('账号已切换，请重新选择照片');
    const today = new Date().toISOString().slice(0, 10);
    const id = `day${nodeDay}`;
    setPhotos(prev => {
      const existing = prev.find(p => p.id === id);
      const next = existing
        ? prev.map(p => p.id === id ? { ...p, dataUrl, takenDate: today } : p)
        : [...prev, { id, nodeDay, dataUrl, takenDate: today }];
      const sorted = next.sort((a, b) => a.nodeDay - b.nodeDay);
      userStorage.setItem(STORAGE_KEY, JSON.stringify(sorted));
      return sorted;
    });
    return dataUrl;
  }, []);

  // 删除某节点照片
  const deletePhoto = useCallback((nodeDay: number) => {
    const id = `day${nodeDay}`;
    setPhotos(prev => {
      const next = prev.filter(p => p.id !== id);
      userStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // 所有节点（含未上传的）
  const nodes = useMemo(
    () => getPhotoNodes(plan.cycleTotalDays || 10, currentCycleDisplay || 1),
    [plan.cycleTotalDays, currentCycleDisplay],
  );

  // 当前是第几天（按日期自动推进）
  const currentDay = useMemo(() => {
    if (!plan.cycleTotalDays || !currentCycleDisplay || !cycleDayDisplay) return 1;
    return (currentCycleDisplay - 1) * plan.cycleTotalDays + cycleDayDisplay;
  }, [plan.cycleTotalDays, currentCycleDisplay, cycleDayDisplay]);

  // 今日是否是拍照节点日
  const isTodayPhotoDay = useMemo(() => {
    return nodes.includes(currentDay);
  }, [nodes, currentDay]);

  // 今日节点是否已上传照片
  const isTodayPhotoTaken = useMemo(() => {
    return photos.some(p => p.nodeDay === currentDay);
  }, [photos, currentDay]);

  // 下一个拍照节点日（离今天最近的未来节点）
  const nextPhotoDay = useMemo(() => {
    const upcoming = nodes.filter(n => n > currentDay);
    return upcoming.length > 0 ? upcoming[0] : null;
  }, [nodes, currentDay]);

  return {
    photos,
    nodes,
    currentDay,
    isTodayPhotoDay,
    isTodayPhotoTaken,
    nextPhotoDay,
    uploadPhoto,
    deletePhoto,
    loaded,
    persist,
  };
}
